import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Bell, LogOut, ChevronDown, Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/layout/PublicNav";
import api from "@/lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

export default function DashboardShell({ menu, title, children }) {
  const { user, logout, setActiveRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const loadNotifs = async () => {
    try { const { data } = await api.get("/notifications"); setNotifs(data); } catch (e) { console.error("Failed to load notifications", e); }
  };
  useEffect(() => { loadNotifs(); }, []);
  const unread = notifs.filter((n) => !n.read).length;

  const switchRole = async (role) => {
    await setActiveRole(role);
    navigate(role === "admin" ? "/admin" : role === "agency" ? "/agency" : "/dashboard");
  };

  const roleLinks = { customer: "/dashboard", agency: "/agency", admin: "/admin" };

  return (
    <div className="min-h-screen flex bg-navy-50">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-navy-900 text-navy-300 flex flex-col transition-transform ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="h-24 flex items-center px-4 border-b border-navy-800">
          <Logo light />
        </div>
        <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto">
          {(menu.some((mm) => mm.to.startsWith("/admin")) && !user?.roles?.includes("admin")
            ? menu.filter((mm) => mm.leadManager) : menu).map((m) => {
            const active = location.pathname === m.to;
            const Icon = m.icon;
            return (
              <Link key={m.to} to={m.to} onClick={() => setSidebarOpen(false)} data-testid={`side-${m.to.replace(/\//g, "-")}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${active ? "bg-brand text-white" : "text-navy-300 hover:bg-navy-800 hover:text-white"}`}>
                <Icon size={18} /> {m.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-navy-800">
          <button onClick={logout} data-testid="logout-btn"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-navy-300 hover:bg-navy-800 hover:text-white w-full">
            <LogOut size={18} /> Log out
          </button>
        </div>
      </aside>
      {sidebarOpen && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-navy-200 flex items-center justify-between px-4 sm:px-6 sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setSidebarOpen(true)} data-testid="sidebar-toggle"><Menu /></button>
            <h1 className="font-heading text-lg font-bold text-navy-900">{title}</h1>
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu onOpenChange={(o) => o && loadNotifs()}>
              <DropdownMenuTrigger asChild>
                <button className="relative p-2 rounded-lg hover:bg-navy-100" data-testid="notif-bell">
                  <Bell size={20} className="text-navy-700" />
                  {unread > 0 && <span className="absolute top-1 right-1 w-4 h-4 bg-brand text-white text-[10px] rounded-full flex items-center justify-center">{unread}</span>}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto">
                <DropdownMenuLabel>Notifications</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {notifs.length === 0 && <p className="px-3 py-6 text-center text-sm text-navy-500">No notifications</p>}
                {notifs.slice(0, 12).map((n) => (
                  <DropdownMenuItem key={n.id} className="flex-col items-start gap-0.5 py-2"
                    onClick={async () => { await api.post(`/notifications/${n.id}/read`); loadNotifs(); }}>
                    <span className="font-semibold text-sm text-navy-900">{n.title}</span>
                    <span className="text-xs text-navy-500">{n.message}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 pl-2 pr-1 py-1.5 rounded-lg hover:bg-navy-100" data-testid="user-menu">
                  <div className="w-8 h-8 rounded-full bg-navy-900 text-white flex items-center justify-center text-sm font-semibold">
                    {(user?.name || "U")[0]}
                  </div>
                  <ChevronDown size={16} className="text-navy-500" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="flex flex-col">
                  <span className="font-semibold">{user?.name}</span>
                  <span className="text-xs font-normal text-navy-500">{user?.email}</span>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {(user?.roles || []).length > 1 && <DropdownMenuLabel className="text-xs text-navy-400">Switch view</DropdownMenuLabel>}
                {(user?.roles || []).map((r) => (
                  <DropdownMenuItem key={r} onClick={() => switchRole(r)} data-testid={`switch-role-${r}`} className="capitalize">
                    {r} portal {user?.active_role === r && "·"}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout}><LogOut size={16} className="mr-2" /> Log out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1400px] w-full mx-auto">{children}</main>
      </div>
    </div>
  );
}
